// The remark plugin behind inline widgets (see ./inlineWidgets.ts): a paragraph that is a widget marker alone becomes a block element
// `ncp-wb` (data `wid`), which MarkdownBody draws through its host's `renderWidget` (or not at all when there is none). A marker anywhere
// else — inside a sentence, a heading or a table cell — is removed: widgets are drawn only where the text gives them a paragraph of their
// own (MarkdownBody first moves such a marker after its paragraph, list or table, as the server does, so this only catches what that did
// not see). Code, inline code and links keep what they show. Hand-written over the mdast (no dependency beyond react-markdown's own).

import { markerOfLine, WIDGET_MARKER } from "./inlineWidgets";

/** The parts of an mdast node this plugin reads (the `mdast` types are not a dependency of this package). */
interface MdNode {
  type: string;
  value?: string;
  children?: MdNode[];
  data?: Record<string, unknown>;
}

/** The hast element a placed widget becomes; MarkdownBody maps it to its widget slot. */
export const WIDGET_ELEMENT = "ncp-wb";

const KEEP = new Set(["code", "inlineCode", "link", "linkReference", "definition", "html"]);

function loneMarker(p: MdNode): string | null {
  const kids = p.children ?? [];
  if (kids.length === 0 || !kids.every((k) => k.type === "text")) return null;
  return markerOfLine(kids.map((k) => k.value ?? "").join(""));
}

function walk(node: MdNode, inTable: boolean): void {
  const kids = node.children;
  if (!kids) return;
  for (const child of kids) {
    if (KEEP.has(child.type)) continue;
    if (child.type === "paragraph" && !inTable) {
      const id = loneMarker(child);
      if (id) {
        child.children = [];
        child.data = { ...(child.data ?? {}), hName: WIDGET_ELEMENT, hProperties: { wid: id } };
        continue;
      }
    }
    if (child.type === "text") {
      const v = child.value ?? "";
      if (v.includes("[[w:")) child.value = v.replace(WIDGET_MARKER, "");
      continue;
    }
    walk(child, inTable || child.type === "table");
  }
}

/** `remarkPlugins={[remarkGfm, remarkInlineWidgets]}`: turns marker paragraphs into widget slots and drops markers found elsewhere. */
export function remarkInlineWidgets() {
  return (tree: unknown) => {
    walk(tree as MdNode, false);
  };
}
